import { Chart, ChartDataset, ChartTypeRegistry, TimeScale, TooltipItem } from 'chart.js';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';

import { Colors } from '~library/prerenderTheme';
import { numberFormatter } from '~library/numbers';
import { dates } from '~library/dates';

export const ColorsList = [
  Colors.Purple5,
  Colors.Magenta5,
  Colors.Cyan6,
  Colors.GeekBlue,
  Colors.Cyan7,
  Colors.Neutral,
  Colors.Orange,
];

// TO BE RUN BEFORE ANY CHARTS ARE CREATED
export function setUpSharedStyles() {
  // Set global default options
  Chart.defaults.responsive = true;
  Chart.defaults.maintainAspectRatio = false;

  Chart.defaults.plugins.legend.display = false;
  const defaultTooltipTitle = Chart.defaults.plugins.tooltip.callbacks.title;
  Chart.defaults.plugins.tooltip.callbacks.title = function (items) {
    return timeScaleTooltipTitle(items) ?? defaultTooltipTitle.call(this, items);
  };
  Chart.defaults.scale.ticks.color = Colors.Gray4;
  Chart.defaults.scales.time.grid = { display: false };

  // BAR CHART
  Chart.defaults.datasets.bar.borderRadius = 2;
  Chart.defaults.datasets.bar.borderWidth = 0;

  // LINE CHART
  Chart.defaults.datasets.line.pointRadius = 0;
}

// doesn't return negative values
export function percentageFormatter(percentage: number): string {
  const absValue = Math.abs(percentage);

  if (absValue <= 0.09) return '< 0.1%';
  return `${numberFormatter.format(absValue)}%`;
}

const TICK_LABEL_BY_GRANULARITY: Record<AggregatedMetrics.IntervalGranularity, (value: number) => string> = {
  hour: dates.hourOfPointInTime,
  day: dates.shortDayOfPointInTime,
  week: dates.shortDayOfPointInTime,
  month: dates.monthOfPointInTime,
};

// A dayjs `tooltipFormat` string can't follow the browser region, so time-scale tooltips are titled here.
function timeScaleTooltipTitle(items: TooltipItem<keyof ChartTypeRegistry>[]): string | undefined {
  const [item] = items;
  const scale = item?.chart.scales.x;
  if (scale?.type !== 'time' || item.parsed.x === null) return undefined;
  const { unit } = (scale as TimeScale).options.time;
  return unit === 'hour' ? dates.pointInTime(item.parsed.x) : dates.dayOfPointInTime(item.parsed.x);
}

export function getXAxisConfig(granularity: AggregatedMetrics.IntervalGranularity) {
  return {
    type: 'time' as const,
    time: {
      unit: granularity,
      round: granularity,
    },
    ticks: {
      source: 'data' as const,
      callback: (value: string | number) => TICK_LABEL_BY_GRANULARITY[granularity](Number(value)),
    },
  };
}

export function getKPIChartDatasetOptions(color: string) {
  const options: Partial<ChartDataset<'line'>> = {
    borderColor: color,
    borderWidth: 1.5,
    pointHoverRadius: (ctx) => {
      const { dataIndex, dataset } = ctx;
      return dataIndex === dataset.data.length - 1 ? 4 : 0;
    },
    pointBackgroundColor: color,
    pointRadius: (ctx) => {
      const { dataIndex, dataset } = ctx;
      return dataIndex === dataset.data.length - 1 ? 4 : 0;
    },
  };

  return options;
}
